import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import moment from "moment";
import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import style from "./ClientStatusTimeline.module.scss";
import { Aviation } from "../../../../../../../services";
import { CURRENCIES } from "../../../../../../../mock";
import { numberToFinanceFormat } from "../../../../../../../utils";
import CustomButton from "../../../../../../custom/custom-button/CustomButton";
import CustomInput from "../../../../../../custom/custom-input/CustomInput";
import ChevronIcon from "../../../../../../shared/icons/ChevronIcon";
import CheckIcon from "../../../../../../shared/icons/CheckIcon";
import CommentIcon from "../../../../../../shared/icons/CommentIcon";
import ClockIcon from "../../../../../../shared/icons/ClockIcon";
import AppTooltip from "../../../../../../shared/AppTooltip/AppTooltip";
import CustomTextarea from "../../../../../../custom/custom-textarea/CustomTextarea";
import ClientBidChangeCard from "./ClientBidChangeCard";

const EMPTY_VALUE = "\u2014";
const hasValue = (value) => value !== undefined && value !== null && value !== "";
const formatDateTime = (value) => (value ? moment(value).format("DD.MM.YYYY, HH:mm") : EMPTY_VALUE);
const formatDate = (value) => (value ? moment(value).format("DD.MM.YYYY") : EMPTY_VALUE);
const formatBidDate = (value) => (value ? moment(value).format("DD.MM.YYYY (HH:mm)") : EMPTY_VALUE);

const STATUS_TO_STEP = {
  awaitingoffer: "tender",
  confirmed: "confirmed",
  accepted: null,
  onthetrip: "onTheTrip",
  delivered: "delivered",
  return: "return",
  completed: "completed",
  canceled: "canceled",
};

const normalizeStatusCode = (statusCode) => (statusCode || "").replace(/\s/g, "").toLowerCase();

const ClientStatusTimeline = ({
  order,
  statusHistory = [],
  bids = [],
  refreshOrderData,
  onForceUpdate,
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [openMap, setOpenMap] = useState({});
  const [activeBidComment, setActiveBidComment] = useState(null);
  const [isDecisionLoading, setIsDecisionLoading] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returnComment, setReturnComment] = useState("");
  const [returnError, setReturnError] = useState("");

  const closeReturnModal = () => {
    setIsReturnModalOpen(false);
    setReturnError("");
  };

  const blocks = useMemo(() => {
    const historyBlocks = statusHistory
      .map((item) => {
        const statusCode = normalizeStatusCode(item.statusCode);
        if (statusCode === "pricechanged") {
          return { ...item, key: "priceChanged" };
        }
        const key = STATUS_TO_STEP[statusCode];
        return key ? { ...item, key } : null;
      })
      .filter(Boolean);

    if (!historyBlocks.some((item) => item.key === "created")) {
      const createdEntry = statusHistory[0];
      historyBlocks.unshift({
        key: "created",
        createdAt: createdEntry?.createdAt || order?.createdAt,
        author: createdEntry?.author,
      });
    }

    const currentKey = STATUS_TO_STEP[normalizeStatusCode(order?.status)];
    const lastStatusKey = [...historyBlocks]
      .reverse()
      .find((item) => item.key !== "priceChanged")?.key;
    if (currentKey && currentKey !== lastStatusKey) {
      historyBlocks.push({ key: currentKey, createdAt: order?.updatedAt });
    }

    return historyBlocks;
  }, [order?.createdAt, order?.status, order?.updatedAt, statusHistory]);

  const handleConfirm = async () => {
    if (!order?.id) return;

    try {
      setIsDecisionLoading(true);
      await Aviation.updateClientStatus(order.id, "completed", "");
      toast.success(t("orderInfoAviation.actions.confirmSuccess"));
      await refreshOrderData?.();
      onForceUpdate?.();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.genericError"));
    } finally {
      setIsDecisionLoading(false);
    }
  };

  const handleReturn = async () => {
    const comment = returnComment.trim();
    if (!comment) {
      setReturnError(t("orderInfoAviation.actions.returnReasonRequired"));
      toast.error(t("orderInfoAviation.actions.returnReasonRequired"));
      return;
    }
    if (!order?.id) return;

    try {
      setIsDecisionLoading(true);
      setReturnError("");
      await Aviation.updateClientStatus(order.id, "return", comment);
      setIsReturnModalOpen(false);
      setReturnComment("");
      toast.success(t("orderInfoAviation.actions.returnSuccess"));
      await refreshOrderData?.();
      onForceUpdate?.();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.genericError"));
    } finally {
      setIsDecisionLoading(false);
    }
  };

  const toggle = (index, defaultOpen) => {
    setOpenMap((current) => ({
      ...current,
      [index]: !(index in current ? current[index] : defaultOpen),
    }));
  };

  const renderBody = (block, isCurrent) => {
    const expeditorName = order?.expeditorName || EMPTY_VALUE;

    switch (block.key) {
      case "created":
        return (
          <div className={style.created_row}>
            <span className={style.created_item}>
              <span>{t("orderInfoAviation.actions.createdBy")} </span>
              <b>{block.author || EMPTY_VALUE}</b>
            </span>
            <span className={style.created_item}>
              <span>{t("orderInfoAviation.actions.createdAt")} </span>
              <b>{formatDate(block.createdAt)}</b>
            </span>
          </div>
        );
      case "tender": {
        const sortedBids = [...bids].sort(
          (firstBid, secondBid) =>
            (secondBid?.isAccepted ? 1 : 0) - (firstBid?.isAccepted ? 1 : 0),
        );

        return (
          <div className={style.bids}>
            <div className={style.bids_head}>
              <span className={style.bids_col_head}>{t("orderInfoAviation.actions.bidCompany")}</span>
              <span className={style.bids_col_head}>{t("orderInfoAviation.actions.bidDate")}</span>
              <span className={style.bids_col_head}>{t("orderInfoAviation.actions.bidAmount")}</span>
              <span className={style.bids_col_head}>{t("orderInfoAviation.actions.bidTerm")}</span>
              <span className={style.bids_col_head}>{t("orderInfoAviation.actions.bidStatus")}</span>
            </div>
            {sortedBids.map((bid, index) => {
              const bidKey = bid?.id || index;
              const companyName = bid?.expeditorName || bid?.userName || EMPTY_VALUE;
              const tooltipId = `aviation-timeline-bid-name-${bidKey}`;
              const hasCommentDetails =
                Boolean(bid?.comments) || hasValue(bid?.weight) || hasValue(bid?.volume);
              const isCommentOpen = activeBidComment === bidKey;

              return (
                <React.Fragment key={bidKey}>
                  <div className={style.bids_row}>
                    <span
                      className={cn(style.bids_cell, style.bids_cell_name)}
                      data-label={t("orderInfoAviation.actions.bidCompany")}
                      data-tooltip-id={tooltipId}
                    >
                      {companyName}
                    </span>
                    <span
                      className={style.bids_cell}
                      data-label={t("orderInfoAviation.actions.bidDate")}
                    >
                      {formatBidDate(bid?.updatedAt || bid?.createdAt)}
                    </span>
                    <span
                      className={style.bids_cell}
                      data-label={t("orderInfoAviation.actions.bidAmount")}
                    >
                      {numberToFinanceFormat(bid?.price)}{" "}
                      {CURRENCIES[bid?.currency || "EUR"]?.symbol || ""}
                    </span>
                    <span
                      className={cn(style.bids_cell, style.bid_term)}
                      data-label={t("orderInfoAviation.actions.bidTerm")}
                    >
                      <span>
                        {bid?.plannedDays
                          ? `${bid.plannedDays} ${t("orderInfoAviation.actions.daysShort")}`
                          : EMPTY_VALUE}
                      </span>
                      {hasCommentDetails ? (
                        <button
                          type="button"
                          className={style.comment_btn}
                          aria-label={t("orderInfoAviation.actions.commentTitle")}
                          aria-expanded={isCommentOpen}
                          onClick={() =>
                            setActiveBidComment((currentKey) =>
                              currentKey === bidKey ? null : bidKey,
                            )
                          }
                        >
                          <CommentIcon type={3} fill="#212121" />
                        </button>
                      ) : null}
                    </span>
                    <span
                      className={bid?.isAccepted ? style.badge_accepted : style.badge_rejected}
                      data-label={t("orderInfoAviation.actions.bidStatus")}
                    >
                      {bid?.isAccepted
                        ? t("orderInfoAviation.actions.acceptedStatus")
                        : t("orderInfoAviation.actions.notAcceptedStatus")}
                    </span>
                  </div>

                  <AppTooltip id={tooltipId} place="top" positionStrategy="fixed">
                    {companyName}
                  </AppTooltip>

                  {isCommentOpen && hasCommentDetails ? (
                    <div className={style.comment_box}>
                      <div className={style.comment_box_head}>
                        {t("orderInfoAviation.actions.commentTitle")}
                      </div>
                      {bid?.comments ? <div className={style.comment_box_text}>{bid.comments}</div> : null}
                      {hasValue(bid?.weight) ? (
                        <div className={style.comment_box_text}>
                          <b>{t("orderInfoAviation.actions.weight")}</b> {bid.weight}
                        </div>
                      ) : null}
                      {hasValue(bid?.volume) ? (
                        <div className={style.comment_box_text}>
                          <b>{t("orderInfoAviation.actions.volume")}</b> {bid.volume}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })}
          </div>
        );
      }
      case "confirmed":
        return (
          <p className={style.text}>
            {t("orderInfoAviation.actions.confirmedText")} <b>{expeditorName}</b>
          </p>
        );
      case "onTheTrip":
        return (
          <>
            <p className={style.text}>{t("orderInfoAviation.actions.onTheTripText")}</p>
            <div className={style.fields}>
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoAviation.actions.unloadingAddress")}</span>
                <span className={style.field_value}>
                  {order?.addressTo || order?.unloadingAddress || order?.cityToName || EMPTY_VALUE}
                </span>
              </div>
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoAviation.actions.estimatedArrival")}</span>
                <span className={style.field_value}>{formatDate(order?.expectedUnloadingDate)}</span>
              </div>
            </div>
          </>
        );
      case "delivered":
        return (
          <>
            <p className={style.text}>
              <b>{expeditorName}</b> {t("orderInfoAviation.actions.deliveredSuffix")}
            </p>
            {isCurrent && user?.type === "Client" ? (
              <div className={style.decision_actions}>
                <CustomButton
                  text={t("orderInfoAviation.actions.confirm")}
                  isNavy
                  style={{ flex: 1 }}
                  onClick={handleConfirm}
                  isLoading={isDecisionLoading}
                  disabled={isDecisionLoading}
                />
                <CustomButton
                  text={t("orderInfoAviation.actions.reject")}
                  isNavy
                  isWhite
                  style={{ flex: 1 }}
                  onClick={() => {
                    setReturnComment("");
                    setReturnError("");
                    setIsReturnModalOpen(true);
                  }}
                  disabled={isDecisionLoading}
                />
              </div>
            ) : null}
          </>
        );
      case "return":
        return (
          <>
            <p className={style.text}>{t("orderInfoAviation.actions.returnedText")}</p>
            <div className={style.comment_box}>
              <div className={style.comment_box_head}>
                <span>{t("orderInfoAviation.actions.yourComment")}</span>
              </div>
              <div className={style.comment_box_text}>
                {block.comment || order?.rejectReason || EMPTY_VALUE}
              </div>
            </div>
          </>
        );
      case "completed":
        return (
          <div className={style.created_row}>
            <span className={style.created_item}>
              <b>{t("orderInfoAviation.actions.completedText")}</b>
            </span>
            <span className={style.created_item}>
              <span>{t("orderInfoAviation.actions.completedAt")} </span>
              <b>{formatDateTime(block.createdAt || order?.updatedAt)}</b>
            </span>
          </div>
        );
      case "canceled":
        return (
          <>
            <div className={style.created_row}>
              <span className={style.created_item}>
                <span>{t("orderInfoAviation.actions.canceledBy")} </span>
                <b>{block.author || EMPTY_VALUE}</b>
              </span>
              <span className={style.created_item}>
                <span>{t("orderInfoAviation.actions.canceledAt")} </span>
                <b>{formatDateTime(block.createdAt || order?.updatedAt)}</b>
              </span>
            </div>
            {block.comment ? (
              <div className={style.comment_box}>
                <div className={style.comment_box_head}>
                  <span>{t("orderInfoAviation.actions.cancelReason")}</span>
                </div>
                <div className={style.comment_box_text}>{block.comment}</div>
              </div>
            ) : null}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={style.timeline} data-testid="aviation-actions-timeline">
      {blocks.map((block, index) => {
        const isLast = index === blocks.length - 1;

        if (block.key === "priceChanged") {
          const priceStatus = (block.changedPriceStatus || "").toLowerCase();
          const isAcceptedPrice = priceStatus === "accepted" || priceStatus === "confirmed";
          const circleClass = isAcceptedPrice
            ? style.circle_done
            : priceStatus === "rejected"
            ? style.circle_priceRejected
            : style.circle_current;
          const circleIcon = isAcceptedPrice ? (
            <CheckIcon />
          ) : priceStatus === "rejected" ? (
            <XIcon size={22} />
          ) : (
            <ClockIcon fill="#fff" type={2} width={24} height={24} />
          );

          return (
            <div className={style.row} key={`priceChanged-${block.createdAt || index}-${index}`}>
              <div className={style.rail}>
                <div className={cn(style.circle, circleClass)}>{circleIcon}</div>
                {!isLast ? <div className={style.line} /> : null}
              </div>
              <div className={style.card_wrap}>
                <ClientBidChangeCard
                  item={block}
                  canDecide={user?.type === "Client"}
                  onDone={async () => {
                    await refreshOrderData?.();
                    onForceUpdate?.();
                  }}
                />
              </div>
            </div>
          );
        }

        const isCompleted = block.key === "completed";
        const isCanceled = block.key === "canceled";
        const isCurrent = isLast && !isCompleted && !isCanceled;
        const defaultOpen = isCurrent || isCompleted || isCanceled;
        const isOpen = index in openMap ? openMap[index] : defaultOpen;

        return (
          <div className={style.row} key={`${block.key}-${block.createdAt || index}-${index}`}>
            <div className={style.rail}>
              <div
                className={cn(style.circle, {
                  [style.circle_done]: !isCurrent && !isCanceled,
                  [style.circle_current]: isCurrent && !isCanceled,
                  [style.circle_rejected]: isCanceled,
                })}
              >
                {isCanceled ? <XIcon size={22} /> : <CheckIcon />}
              </div>
              {!isLast ? <div className={style.line} /> : null}
            </div>
            <div className={style.card_wrap}>
              <div className={cn(style.card, { [style.card_current]: isCurrent })}>
                <button
                  type="button"
                  className={cn(style.header, { [style.header_open]: isOpen })}
                  onClick={() => toggle(index, defaultOpen)}
                >
                  <span className={style.header_left}>
                    <span className={style.title}>
                      {t(`orderInfoAviation.actions.stepTitles.${block.key}`)}
                    </span>
                    {isCurrent ? (
                      <span className={style.now_badge}>
                        <span className={style.now_dot} />
                        <span className={style.now_text}>{t("orderInfoAviation.actions.now")}</span>
                      </span>
                    ) : null}
                  </span>
                  <span className={style.header_right}>
                    {block.createdAt ? <span className={style.time}>{formatDateTime(block.createdAt)}</span> : null}
                    <span className={cn(style.chevron, { [style.chevron_open]: isOpen })}>
                      <ChevronIcon />
                    </span>
                  </span>
                </button>
                {isOpen ? <div className={style.body}>{renderBody(block, isCurrent)}</div> : null}
              </div>
            </div>
          </div>
        );
      })}

      {isReturnModalOpen
        ? ReactDOM.createPortal(
            <div
              className={style.modal_overlay}
              data-portal-modal="true"
              onClick={closeReturnModal}
            >
              <div
                className={style.modal}
                role="dialog"
                aria-label={t("orderInfoAviation.actions.returnModalTitle")}
                onClick={(event) => event.stopPropagation()}
              >
                <div className={style.modal_topbar}>
                  <h4 className={style.modal_title}>{t("orderInfoAviation.actions.returnModalTitle")}</h4>
                  <button
                    type="button"
                    className={style.modal_close}
                    onClick={closeReturnModal}
                    aria-label={t("common.cancel")}
                  >
                    <XIcon size={24} />
                  </button>
                </div>
                <div className={style.modal_field_card}>
                  <CustomTextarea
                    labelText={`${t("orderInfoAviation.actions.returnReasonLabel")}*`}
                    placeholder={t("orderInfoAviation.actions.returnReasonPlaceholder")}
                    value={returnComment}
                    onChangeValue={(value) => {
                      setReturnComment(value);
                      if (returnError) setReturnError("");
                    }}
                    errorText={returnError}
                    isBgGray
                    inputType="text"
                  />
                </div>
                <div className={style.modal_actions}>
                  <CustomButton
                    text={t("common.cancel")}
                    isWhite
                    onClick={() => setIsReturnModalOpen(false)}
                  />
                  <CustomButton
                    text={t("orderInfoAviation.actions.send")}
                    isNavy
                    onClick={handleReturn}
                    isLoading={isDecisionLoading}
                    disabled={!returnComment.trim() || isDecisionLoading}
                  />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default ClientStatusTimeline;
